import { startStatsSampler } from './stats';
import type { OutboundMessage } from './signaling';
import type { IceConfigResponse, ParticipantConnectionStats } from './types';

type SignalPayload =
  | Extract<OutboundMessage, { type: 'webrtc:offer' }>
  | Extract<OutboundMessage, { type: 'webrtc:answer' }>
  | Extract<OutboundMessage, { type: 'webrtc:ice' }>
  | Extract<OutboundMessage, { type: 'webrtc:restart-request' }>;

function iceServers(config: IceConfigResponse): RTCIceServer[] {
  return config.iceServers.map((server) => ({
    urls: server.urls,
    ...(server.username ? { username: server.username } : {}),
    ...(server.credential ? { credential: server.credential } : {}),
  }));
}

export function createPeerConnection(config: IceConfigResponse): RTCPeerConnection {
  return new RTCPeerConnection({
    iceServers: iceServers(config),
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
    iceCandidatePoolSize: 2,
  });
}

export type BroadcasterCallbacks = {
  onSignal: (payload: SignalPayload) => void;
  onPeerState: (viewerId: string, state: RTCPeerConnectionState) => void;
  onPeerStats: (viewerId: string, stats: ParticipantConnectionStats) => void;
  onError: (message: string) => void;
};

type SenderPeer = {
  pc: RTCPeerConnection;
  senders: Map<string, RTCRtpSender>;
  pendingIce: RTCIceCandidateInit[];
  stopStats: () => void;
  restartTimer: number | null;
  restartAttempts: number;
};

/**
 * Host side: one RTCPeerConnection per viewer, all sharing the same captured
 * MediaStream. Video is never sent through the signaling server.
 */
export class StreamBroadcaster {
  private readonly callbacks: BroadcasterCallbacks;
  private roomId: string;
  private iceConfig: IceConfigResponse;
  private stream: MediaStream | null = null;
  private bitrateKbps: number | null = null;
  private readonly peers = new Map<string, SenderPeer>();

  constructor(roomId: string, iceConfig: IceConfigResponse, callbacks: BroadcasterCallbacks) {
    this.roomId = roomId;
    this.iceConfig = iceConfig;
    this.callbacks = callbacks;
  }

  setIceConfig(config: IceConfigResponse): void {
    this.iceConfig = config;
  }

  getIceConfig(): IceConfigResponse {
    return this.iceConfig;
  }

  setRoomId(roomId: string): void {
    this.roomId = roomId;
  }

  setStream(stream: MediaStream): void {
    this.stream = stream;
    const tracks = stream.getTracks();
    for (const [viewerId, peer] of this.peers) {
      void this.syncTracks(viewerId, peer, tracks);
    }
  }

  getStream(): MediaStream | null {
    return this.stream;
  }

  addViewer(viewerId: string): void {
    if (this.peers.has(viewerId)) return;
    if (!this.stream) return;

    const pc = createPeerConnection(this.iceConfig);
    const peer: SenderPeer = {
      pc,
      senders: new Map(),
      pendingIce: [],
      stopStats: () => undefined,
      restartTimer: null,
      restartAttempts: 0,
    };
    this.peers.set(viewerId, peer);

    for (const track of this.stream.getTracks()) {
      const sender = pc.addTrack(track, this.stream);
      peer.senders.set(track.kind, sender);
    }

    pc.addEventListener('icecandidate', (event) => {
      this.callbacks.onSignal({
        type: 'webrtc:ice',
        roomId: this.roomId,
        targetUserId: viewerId,
        candidate: event.candidate ? event.candidate.toJSON() : null,
      });
    });
    pc.addEventListener('icecandidateerror', () => {
      // ICE restart/connection state handlers cover recoverable failures.
    });
    pc.addEventListener('connectionstatechange', () => {
      this.callbacks.onPeerState(viewerId, pc.connectionState);
      if (pc.connectionState === 'connected') {
        peer.restartAttempts = 0;
        void this.applyEncoding();
      } else if (pc.connectionState === 'failed') {
        this.scheduleRestart(viewerId, peer, 1200);
      } else if (pc.connectionState === 'disconnected') {
        this.scheduleRestart(viewerId, peer, 4000);
      }
    });

    peer.stopStats = startStatsSampler(
      pc,
      'sender',
      (stats) => this.callbacks.onPeerStats(viewerId, stats),
      () => undefined,
    );

    void this.createOffer(viewerId, peer);
  }

  removeViewer(viewerId: string): void {
    const peer = this.peers.get(viewerId);
    if (!peer) return;
    this.peers.delete(viewerId);
    if (peer.restartTimer != null) window.clearTimeout(peer.restartTimer);
    peer.stopStats();
    try {
      peer.pc.close();
    } catch {
      // Already closed.
    }
  }

  handleAnswer(viewerId: string, sdp: RTCSessionDescriptionInit): void {
    const peer = this.peers.get(viewerId);
    if (!peer) return;
    void (async () => {
      try {
        if (peer.pc.signalingState === 'have-local-offer') {
          await peer.pc.setRemoteDescription(sdp);
          await this.flushIce(peer);
          await this.applyEncoding();
        }
      } catch (error) {
        this.callbacks.onError(
          `Не удалось применить ответ зрителя: ${error instanceof Error ? error.message : 'ошибка WebRTC'}`,
        );
      }
    })();
  }

  handleIce(viewerId: string, candidate: RTCIceCandidateInit | null): void {
    const peer = this.peers.get(viewerId);
    if (!peer) return;
    if (!peer.pc.remoteDescription) {
      if (candidate) peer.pendingIce.push(candidate);
      return;
    }
    void this.addIce(peer, candidate);
  }

  async restartPeer(viewerId: string): Promise<void> {
    const peer = this.peers.get(viewerId);
    if (!peer) return;
    if (peer.pc.connectionState === 'closed') return;
    if (peer.pc.signalingState !== 'stable') return;
    try {
      peer.pc.restartIce();
      const offer = await peer.pc.createOffer({ iceRestart: true });
      await peer.pc.setLocalDescription(offer);
      this.sendOffer(viewerId, peer);
    } catch {
      // The next connection state transition may retry.
    }
  }

  setBitrate(bitrateKbps: number): void {
    this.bitrateKbps = bitrateKbps;
    void this.applyEncoding();
  }

  async applyEncoding(bitrateKbps: number | null = this.bitrateKbps): Promise<void> {
    this.bitrateKbps = bitrateKbps;
    for (const [viewerId, peer] of this.peers) {
      await this.applyEncodingToPeer(viewerId, peer, bitrateKbps);
    }
  }

  getViewerIds(): string[] {
    return [...this.peers.keys()];
  }

  resetPeers(): void {
    for (const viewerId of [...this.peers.keys()]) {
      this.removeViewer(viewerId);
    }
  }

  close(): void {
    this.resetPeers();
    this.stream = null;
  }

  private async createOffer(viewerId: string, peer: SenderPeer): Promise<void> {
    try {
      if (peer.pc.signalingState === 'closed') return;
      const offer = await peer.pc.createOffer();
      await peer.pc.setLocalDescription(offer);
      this.sendOffer(viewerId, peer);
    } catch (error) {
      this.callbacks.onError(
        `Не удалось создать WebRTC offer: ${error instanceof Error ? error.message : 'ошибка WebRTC'}`,
      );
    }
  }

  private sendOffer(viewerId: string, peer: SenderPeer): void {
    const description = peer.pc.localDescription;
    if (!description?.sdp) return;
    this.callbacks.onSignal({
      type: 'webrtc:offer',
      roomId: this.roomId,
      targetUserId: viewerId,
      sdp: { type: 'offer', sdp: description.sdp },
    });
  }

  private async syncTracks(
    viewerId: string,
    peer: SenderPeer,
    tracks: MediaStreamTrack[],
  ): Promise<void> {
    if (!this.stream) return;
    let needsRenegotiation = false;
    const incomingKinds = new Set(tracks.map((track) => track.kind));

    for (const track of tracks) {
      const sender = peer.senders.get(track.kind);
      if (sender) {
        try {
          await sender.replaceTrack(track);
        } catch {
          this.callbacks.onError('Не удалось заменить видеотрек в WebRTC-соединении.');
        }
      } else {
        const newSender = peer.pc.addTrack(track, this.stream);
        peer.senders.set(track.kind, newSender);
        needsRenegotiation = true;
      }
    }

    for (const [kind, sender] of peer.senders) {
      if (!incomingKinds.has(kind)) {
        try {
          await sender.replaceTrack(null);
        } catch {
          // Ignore.
        }
      }
    }

    if (needsRenegotiation && peer.pc.signalingState === 'stable') {
      const offer = await peer.pc.createOffer();
      await peer.pc.setLocalDescription(offer);
      this.sendOffer(viewerId, peer);
    }
  }

  private scheduleRestart(
    viewerId: string,
    peer: SenderPeer,
    delay: number,
  ): void {
    if (peer.restartTimer != null) return;
    if (peer.restartAttempts >= 5) return;
    peer.restartTimer = window.setTimeout(() => {
      peer.restartTimer = null;
      if (!this.peers.has(viewerId)) return;
      if (!['failed', 'disconnected'].includes(peer.pc.connectionState)) return;
      peer.restartAttempts += 1;
      void this.restartPeer(viewerId);
      if (peer.pc.connectionState === 'failed') {
        this.scheduleRestart(viewerId, peer, Math.min(2000 * peer.restartAttempts, 8000));
      }
    }, delay);
  }

  private async flushIce(peer: SenderPeer): Promise<void> {
    const pending = peer.pendingIce.splice(0);
    for (const candidate of pending) {
      await this.addIce(peer, candidate);
    }
  }

  private async addIce(peer: SenderPeer, candidate: RTCIceCandidateInit | null): Promise<void> {
    try {
      await peer.pc.addIceCandidate(candidate ?? undefined);
    } catch {
      // Stale or duplicate candidates are expected during reconnects.
    }
  }

  private async applyEncodingToPeer(
    _viewerId: string,
    peer: SenderPeer,
    bitrateKbps: number | null,
  ): Promise<void> {
    for (const sender of peer.senders.values()) {
      if (!sender.track || sender.track.kind !== 'video') continue;
      try {
        const parameters = sender.getParameters();
        if (!parameters.encodings || parameters.encodings.length === 0) {
          parameters.encodings = [{}];
        }
        if (bitrateKbps && bitrateKbps > 0) {
          parameters.encodings[0]!.maxBitrate = bitrateKbps * 1000;
        }
        try {
          parameters.degradationPreference = 'maintain-resolution';
          await sender.setParameters(parameters);
        } catch {
          // Firefox and some Chromium builds do not support this field.
          parameters.degradationPreference = undefined;
          await sender.setParameters(parameters);
        }
      } catch {
        // Encoding parameters become available after negotiation; a later
        // 'connected' transition applies them again.
      }
    }
  }
}

export type ReceiverCallbacks = {
  onSignal: (payload: SignalPayload) => void;
  onTrack: (stream: MediaStream) => void;
  onState: (state: RTCPeerConnectionState) => void;
  onStats: (stats: ParticipantConnectionStats) => void;
  onError: (message: string) => void;
};

/**
 * Viewer side. On every incoming offer it starts a fresh peer connection;
 * this is safe because the host is always the offerer and rebuilds the
 * matching connection state after a reconnect.
 */
export class StreamReceiver {
  private readonly roomId: string;
  private readonly callbacks: ReceiverCallbacks;
  private iceConfig: IceConfigResponse;
  private pc: RTCPeerConnection | null = null;
  private remoteStream: MediaStream | null = null;
  private pendingIce: RTCIceCandidateInit[] = [];
  private stopStats: (() => void) | null = null;
  private operation: Promise<void> = Promise.resolve();
  private restartRequestTimer: number | null = null;
  private stopped = false;
  private hostId: string | null = null;

  constructor(roomId: string, iceConfig: IceConfigResponse, callbacks: ReceiverCallbacks) {
    this.roomId = roomId;
    this.iceConfig = iceConfig;
    this.callbacks = callbacks;
  }

  setIceConfig(config: IceConfigResponse): void {
    this.iceConfig = config;
  }

  handleOffer(fromUserId: string, sdp: RTCSessionDescriptionInit): void {
    if (this.stopped) return;
    this.operation = this.operation
      .then(() => this.processOffer(fromUserId, sdp))
      .catch((error) => {
        this.callbacks.onError(
          `Не удалось принять WebRTC offer: ${error instanceof Error ? error.message : 'ошибка WebRTC'}`,
        );
      });
  }

  handleIce(candidate: RTCIceCandidateInit | null): void {
    if (this.stopped) return;
    if (!this.pc || !this.pc.remoteDescription) {
      if (candidate) this.pendingIce.push(candidate);
      return;
    }
    void this.addIce(candidate);
  }

  stop(): void {
    this.stopped = true;
    if (this.restartRequestTimer != null) window.clearTimeout(this.restartRequestTimer);
    this.stopStats?.();
    this.stopStats = null;
    try {
      this.pc?.close();
    } catch {
      // Ignore.
    }
    this.pc = null;
  }

  private async processOffer(
    fromUserId: string,
    sdp: RTCSessionDescriptionInit,
  ): Promise<void> {
    if (this.stopped) return;
    this.hostId = fromUserId;
    this.stopStats?.();
    this.stopStats = null;
    if (this.pc) {
      try {
        this.pc.close();
      } catch {
        // Ignore.
      }
    }

    const pc = createPeerConnection(this.iceConfig);
    this.pc = pc;
    const stream = new MediaStream();
    this.remoteStream = stream;

    pc.addEventListener('track', (event) => {
      for (const track of event.streams[0]?.getTracks() ?? [event.track]) {
        if (!stream.getTracks().some((existing) => existing.id === track.id)) {
          stream.addTrack(track);
        }
      }
      this.callbacks.onTrack(stream);
    });
    pc.addEventListener('connectionstatechange', () => {
      this.callbacks.onState(pc.connectionState);
      if (pc.connectionState === 'failed') {
        this.scheduleRestartRequest();
      }
    });
    pc.addEventListener('icecandidate', (event) => {
      this.callbacks.onSignal({
        type: 'webrtc:ice',
        roomId: this.roomId,
        targetUserId: fromUserId,
        candidate: event.candidate ? event.candidate.toJSON() : null,
      });
    });

    this.stopStats = startStatsSampler(
      pc,
      'receiver',
      (stats) => this.callbacks.onStats(stats),
      () => undefined,
    );
    this.callbacks.onState('new');

    await pc.setRemoteDescription(sdp);
    for (const candidate of this.pendingIce.splice(0)) {
      await this.addIce(candidate);
    }
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    if (!answer.sdp) throw new Error('Браузер создал пустой SDP answer');
    this.callbacks.onSignal({
      type: 'webrtc:answer',
      roomId: this.roomId,
      targetUserId: fromUserId,
      sdp: { type: 'answer', sdp: answer.sdp },
    });
    this.callbacks.onTrack(stream);
  }

  private scheduleRestartRequest(): void {
    if (this.restartRequestTimer != null || this.stopped || !this.hostId) return;
    this.restartRequestTimer = window.setTimeout(() => {
      this.restartRequestTimer = null;
      if (this.stopped || !this.hostId) return;
      this.callbacks.onSignal({
        type: 'webrtc:restart-request',
        roomId: this.roomId,
        targetUserId: this.hostId,
      });
    }, 3000);
  }

  private async addIce(candidate: RTCIceCandidateInit | null): Promise<void> {
    if (!this.pc) return;
    try {
      await this.pc.addIceCandidate(candidate ?? undefined);
    } catch {
      // Stale candidates from a previous session are expected.
    }
  }
}
